import {AsyncPipe, CurrencyPipe} from '@angular/common';
import {Component} from '@angular/core';
import {TranslationService} from '@emma-labs/shared/angular';
import {Duration, durationToNumber, getOrdinalSuffix} from '@emma-labs/shared/typescript';
import {DynamicDialogConfig} from 'primeng/dynamicdialog';
import {SkeletonModule} from 'primeng/skeleton';
import {first, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ProtectionService} from '../../../../../../services/protection.service';

@Component({
  selector: 'em-renewal-prices',
  templateUrl: './renewal-prices.component.html',
  styleUrls: ['./renewal-prices.component.scss'],
  imports: [CurrencyPipe, AsyncPipe, SkeletonModule],
})
export class RenewalPricesComponent {
  renewalPricesObjects$: Observable<{duration: Duration; price: number; renewalPrices: number[]}[]>;

  constructor(
    private readonly protectionService: ProtectionService,
    private readonly translate: TranslationService,
    readonly config: DynamicDialogConfig,
  ) {
    const {
      ratesVersion,
      coverages,
      providerId,
      categoryCodes,
      age,
      smoker,
      sex,
      termCoverageDuration,
      defaultShownDurations,
      hasWholeLife,
    } = config.data;

    this.renewalPricesObjects$ = this.protectionService
      .getProtections(ratesVersion, coverages, providerId, categoryCodes, age, smoker, sex, {
        withRenewalPrices: true,
        allowPartialSuccess: true,
      })
      .pipe(
        first(),
        map((protections) => {
          const map: Map<Duration, {price: number; renewalPrices: number[]}> = new Map();

          protections.forEach((protection) => {
            protection.personInsurances
              .filter((pi) => pi.renewalPrices)
              .forEach((pi) => {
                map.set(pi.duration, {
                  price: pi.priceBreakdown.basePremium + (hasWholeLife ? 0 : pi.priceBreakdown.policyFee),
                  renewalPrices: pi.renewalPrices!.map(
                    (rp) => rp - (hasWholeLife ? pi.priceBreakdown.policyFee : 0),
                  ),
                });
              });
          });

          const result: {duration: Duration; price: number; renewalPrices: number[]}[] = [];

          [termCoverageDuration].concat(defaultShownDurations).forEach((duration) => {
            if (map.has(duration)) {
              const {price, renewalPrices} = map.get(duration)!;
              map.delete(duration);
              result.push({duration, renewalPrices, price});
            }
          });

          [...map.entries()].forEach(([duration, {price, renewalPrices}]) => {
            result.push({duration, renewalPrices, price});
          });

          return result;
        }),
      );
  }

  getRenewalYearText(duration: Duration): string {
    const renewalYear = durationToNumber(duration) + 1;

    return this.translate.currentLanguageIsFr()
      ? `${renewalYear}${getOrdinalSuffix(renewalYear, 'fr', true)} année`
      : `${renewalYear}${getOrdinalSuffix(renewalYear, 'en')} year`;
  }
}
